/**
 * ErrorBoundary — per-page render error isolation (Pattern 8.1 / NFR-RESIL-FE-01).
 *
 * Wraps each top-level page independently so a render error in one page does
 * not crash the entire application. The fallback UI provides a user-visible
 * error card and a reload action.
 *
 * Implemented as a React class component because the error boundary API
 * (getDerivedStateFromError / componentDidCatch) requires class components.
 *
 * Security note: the raw error message is shown only in development builds
 * (import.meta.env.DEV) to avoid leaking implementation details (NFR-SEC-FE-01).
 */
import { Component, type ReactNode } from 'react';
import type { ErrorInfo } from 'react';

interface Props {
  children: ReactNode;
  /** Page name used in the error message and console log */
  pageName: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Log to console in development and production.
    // In production this could be forwarded to an error-reporting service.
    // Never logs sensitive data (NFR-SEC-FE-01).
    console.error(`[ErrorBoundary] ${this.props.pageName}:`, error, info);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div
          role="alert"
          aria-live="assertive"
          style={{
            margin: '2rem auto',
            maxWidth: '600px',
            padding: '1.5rem',
            border: '1px solid #fca5a5',
            borderRadius: '0.5rem',
            background: '#fef2f2',
            color: '#991b1b',
            fontFamily: 'system-ui, sans-serif',
          }}
        >
          <h2 style={{ marginBottom: '0.5rem', fontSize: '1.125rem' }}>
            ⚠ Something went wrong
          </h2>
          <p style={{ marginBottom: '1rem' }}>
            {this.props.pageName} encountered an unexpected error.
          </p>
          {import.meta.env.DEV && this.state.error && (
            <pre
              style={{
                marginBottom: '1rem',
                padding: '0.75rem',
                background: '#fee2e2',
                borderRadius: '0.25rem',
                fontSize: '0.75rem',
                overflowX: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {this.state.error.message}
            </pre>
          )}
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '0.5rem 1rem',
              background: '#dc2626',
              color: '#ffffff',
              border: 'none',
              borderRadius: '0.375rem',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '600',
            }}
          >
            Reload page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
